import siteApiV4 from "src/api/clients/site-api-v4";
import { SiteDetails } from "src/features/site/types/responses/site-details";
import { SiteOnboardingPayload } from "../../../../types/payloads/onboarding-payload";
import { IntegrationQuestionnairePayload } from "../../../site-settings/types/payloads/integration-questionnaire";
import { SiteIntegrationQuestionnaireState } from "../../types/enum/site-integration-questionnaire-state";
import { CreateSitePayload } from "../../types/payloads/sites/create-site-payload";
import { ApproveSiteRemoval } from "../../types/responses/approve-site-removal";
import { IntegrationQuestionnaire } from "../../types/responses/integration-questionnaire";
import { IntegrationQuestionnaireQuestion } from "../../types/responses/integration-questionnaire-question";
import { RemoveSite } from "../../types/responses/remove-site";
import { RemoveSiteStatus } from "../../types/responses/remove-site-status";
import { Address, Site, SiteWithTenants } from "../../types/responses/site";

class SitesApi {
  async fetchSites(): Promise<Site[]> {
    const { data } = await siteApiV4.get<Site[]>("/sites");

    return data;
  }

  async createSite(values: CreateSitePayload): Promise<Site> {
    const { data } = await siteApiV4.post<Site>(`/sites`, {
      name: values.name,
      streetName: values.streetName,
      buildingNumber: values.buildingNumber,
      postcode: values.postcode,
      cityName: values.cityName,
      timezone: values.timezone,
    });

    return data;
  }

  async fetchSite(siteToken: string): Promise<SiteWithTenants> {
    const { data } = await siteApiV4.get<SiteWithTenants>(`/sites/${siteToken}`);

    return data;
  }

  async updateSite(
    token: string,
    name: string,
    address: Address,
    timezone?: string,
  ): Promise<Site> {
    const { data } = await siteApiV4.patch<Site>(`/sites/${token}`, {
      name,
      timezone,
      ...address,
    });
    return data;
  }

  async fetchSiteDetails(siteToken: string): Promise<SiteDetails> {
    const { data } = await siteApiV4.get<SiteDetails>(`/sites/${siteToken}/information`);

    return data;
  }

  async removeSite(siteName: string): Promise<RemoveSite> {
    const { data } = await siteApiV4.post<RemoveSite>(`/sites/remove`, {
      siteName,
    });

    return data;
  }

  async removeSiteStatus(): Promise<RemoveSiteStatus> {
    const { data } = await siteApiV4.get<RemoveSiteStatus>(`/sites/remove/status`);

    return data;
  }

  async approveRemoveSite(): Promise<ApproveSiteRemoval> {
    const { data } = await siteApiV4.post<ApproveSiteRemoval>(`/sites/remove/approve`);

    return data;
  }

  async rejectRemoveSite(): Promise<void> {
    await siteApiV4.delete<void>(`/sites/remove/reject`);
  }

  async uploadIntegrationQuestionnaireImages(images: FormData): Promise<{ url: string }[]> {
    const { data } = await siteApiV4.post<{ url: string }[]>(
      `/sites/integration-questionnaire/photos`,
      images,
    );

    return data;
  }

  async fetchIntegrationQuestionnaire(siteToken: string): Promise<IntegrationQuestionnaire> {
    const { data } = await siteApiV4.get<IntegrationQuestionnaire>(
      `/sites/${siteToken}/integration-questionnaire`,
    );
    return data;
  }

  async fetchIntegrationQuestionnaireQuestions(): Promise<IntegrationQuestionnaireQuestion[]> {
    const { data } = await siteApiV4.get<IntegrationQuestionnaireQuestion[]>(
      `/sites/integration-questionnaire/questions`,
    );

    return data;
  }

  async sendIntegrationQuestionnaire(
    siteToken: string,
    data: {
      questionnaire: IntegrationQuestionnairePayload[];
      photo1?: string;
      photo2?: string;
      state: SiteIntegrationQuestionnaireState;
    },
  ): Promise<void> {
    await siteApiV4.post<void>(`/sites/${siteToken}/integration-questionnaire`, data);
  }

  async completeRegistration(values: SiteOnboardingPayload): Promise<void> {
    await siteApiV4.post(`/sites/complete-registration`, values);
  }
}

export const sitesApi = new SitesApi();
